/**
 * registerVersionToast — the app-facing preset that couples stale-deploy detection
 * (`@butter/library/client`) to the "New Version Available" countdown toast.
 *
 * Each client app calls this once at startup instead of re-wiring the detector + toast by
 * hand. It stays free of `@butter/monitoring`: the app injects an `onBrokenDeploy` reporter
 * (mirrors the injected-reporter convention in domains/booking/BookingError).
 */
import { registerAppVersionRecovery } from '@butter/library/client'

import { butterToast } from './butterToast'

/**
 * Fired at most once per page session, and only when a reload did NOT resolve the stale
 * chunk (`canAutoRefresh === false`) — i.e. a genuinely broken deploy, not staleness.
 * Resets on the next full page load.
 */
let brokenDeployReported = false

export interface RegisterVersionToastOptions {
  /**
   * Called (at most once per session) when a reload did NOT resolve the stale chunk —
   * usually a broken deploy, occasionally a persistent flaky network. Worth a warning-level
   * signal. Wire it to monitoring, e.g. `captureMessage('…', 'warning', { tags })`. Keeps
   * `@butter/ui` monitoring-free.
   *
   * NOTE: the message must NOT contain "Failed to fetch" / "NetworkError" / "Network request
   * failed" — Sentry's `ignoreErrors` substring-matches those and would silently drop it.
   */
  onBrokenDeploy?: () => void
}

/**
 * Register the version-recovery toast. Safe to call once at app startup. On the native
 * mobile shell, guard the call with `!Capacitor.isNativePlatform()` — OTA is the update
 * path there and a reload can't fix a missing local-bundle chunk.
 *
 * ORDERING CONTRACT: call this at module-eval time in `main.tsx` — BEFORE `initMonitoring`
 * and before React mounts. The detector claims chunk `unhandledrejection`s via
 * `stopImmediatePropagation()`, which only suppresses later-registered `window` listeners
 * (Sentry's global handlers, the app's GlobalErrorHandler). Registering after monitoring
 * init or from inside a component would let chunk errors double-report.
 */
export function registerVersionToast(opts: RegisterVersionToastOptions = {}): void {
  registerAppVersionRecovery({
    onDetected: ({ reload, canAutoRefresh }) => {
      if (!canAutoRefresh && !brokenDeployReported) {
        brokenDeployReported = true
        opts.onBrokenDeploy?.()
      }
      butterToast.countdown({
        id: 'app-new-version',
        title: 'New Version Available',
        message: 'A refresh is required',
        actionLabel: 'Update now',
        // A recent reload that didn't fix it → require a manual click (no auto-loop).
        seconds: canAutoRefresh ? 30 : undefined,
        onComplete: reload,
      })
    },
  })
}
